import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Link, Route, Routes, useLocation } from 'react-router-dom';
import { Landing } from './pages/Landing.js';
import { Host } from './pages/Host.js';
import { Play } from './pages/Play.js';
import { Solo } from './pages/Solo.js';
import { Help } from './pages/Help.js';
import { Privacy } from './pages/Privacy.js';
import { Press } from './pages/Press.js';
import { Toasts } from './components/Toasts.js';
import { SiteHeader } from './components/SiteHeader.js';
import { SiteFooter } from './components/SiteFooter.js';
import { warmVoices } from './lib/tts.js';
import { t, useLocale } from './lib/i18n.js';
import { trackArrival } from './lib/analytics.js';
import './styles.css';
import './launch.css';
import './explanation.css';

warmVoices();
function RouteEffects() {
  const location = useLocation();
  const locale = useLocale();
  useEffect(() => {
    trackArrival();
    if (location.hash)
      requestAnimationFrame(() =>
        document.getElementById(location.hash.slice(1))?.scrollIntoView(),
      );
    else window.scrollTo(0, 0);
    document.title = `${location.pathname === '/daily' ? t('Daily challenge', '今日の一問') : location.pathname === '/solo' || location.pathname === '/odd' ? t('Play a round', '一人で遊ぶ') : t('Hear the clues. Beat the picture.', '聞いて、絵が完成する前に押せ。')} · Listen & Draw`;
  }, [location.pathname, location.hash, locale]);
  return null;
}
function NotFound() {
  useLocale();
  return (
    <main className="page reading-page">
      <p className="eyebrow">404</p>
      <h1>{t('This page wandered off.', 'このページは見つかりません。')}</h1>
      <p>{t('A fresh listening challenge is right here.', '新しいリスニングの挑戦はこちら。')}</p>
      <Link className="btn btn--primary" to="/solo">
        {t('Try a round →', '一人で遊ぶ →')}
      </Link>
    </main>
  );
}
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    if (this.state.failed)
      return (
        <main className="page reading-page">
          <h1>{t('Let’s try that again.', 'もう一度お試しください。')}</h1>
          <p>
            {t(
              'Something went wrong. Reload to start a fresh round.',
              '問題が発生しました。再読み込みして新しいラウンドを始められます。',
            )}
          </p>
          <a className="btn btn--primary" href="/">
            {t('Reload the game', 'ゲームを再読み込み')}
          </a>
        </main>
      );
    return this.props.children;
  }
}
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <a className="skip-link" href="#main-content">
          Skip to content / 本文へ
        </a>
        <SiteHeader />
        <RouteEffects />
        <div id="main-content">
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/solo" element={<Solo />} />
            <Route path="/odd" element={<Solo odd />} />
            <Route path="/daily" element={<Solo daily />} />
            <Route path="/host/:code" element={<Host />} />
            <Route path="/play/:code?" element={<Play />} />
            <Route path="/help" element={<Help />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/press" element={<Press />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        <SiteFooter />
        <Toasts />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
);
